import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { ShieldAlert } from "lucide-react";

export default function Cybersecurity360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/cyber/soc").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Cyber Security Operations Centre"
                title="Zero-Trust SOC · Threat Intelligence"
                description="SIEM, EDR, IdP, firewall and OT SIEM feeds unified in one governed pane — with privileged-access monitoring, ransomware-resilience posture, and vulnerability governance."
            />
            <KpiGrid kpis={data?.kpis || []} accent="red" cols={6} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Zero-Trust Posture" accent="purple">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        {[
                            { k: "Explicit verification", v: "Enforced", s: "normal" },
                            { k: "Least privilege", v: "Enforced", s: "normal" },
                            { k: "Assume breach", v: "Enforced", s: "normal" },
                            { k: "MFA coverage", v: "99.2%", s: "normal" },
                            { k: "PAM coverage", v: "100%", s: "normal" },
                            { k: "OT segmentation", v: "Verified", s: "normal" },
                            { k: "Immutable audit log", v: "Enabled", s: "normal" },
                            { k: "Backup restore test", v: "Passed", s: "normal" },
                        ].map((r) => (
                            <div key={r.k} className="flex items-center justify-between p-2.5 rounded border border-white/5">
                                <span className="kcc-text-silver text-xs">{r.k}</span>
                                <StatusChip status={r.s}>{r.v}</StatusChip>
                            </div>
                        ))}
                    </div>
                </Panel>
                <Panel title="Vulnerability Register" accent="amber" className="xl:col-span-2">
                    <DataTable
                        columns={[
                            { key: "cve", label: "CVE" },
                            { key: "cvss", label: "CVSS", render: (r) => <span className={r.cvss >= 9 ? "text-kcc-red font-mono-kcc" : r.cvss >= 7 ? "text-kcc-amber font-mono-kcc" : "font-mono-kcc kcc-text-silver"}>{r.cvss}</span> },
                            { key: "asset_class", label: "Asset Class" },
                            { key: "affected_hosts", label: "Affected" },
                            { key: "patched_hosts", label: "Patched" },
                            { key: "due", label: "Due", render: (r) => r.due.slice(0, 10) },
                            { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        ]}
                        rows={data?.vulns || []}
                        testid="cyber-vulns"
                    />
                </Panel>
            </div>

            <Panel title="SOC Event Log" subtitle="Live cross-source security events" accent="red">
                <DataTable
                    columns={[
                        { key: "ref", label: "Ref" },
                        { key: "ts", label: "When", render: (r) => r.ts.slice(0, 16).replace("T", " ") },
                        { key: "type", label: "Event" },
                        { key: "source", label: "Source" },
                        { key: "asset", label: "Asset", render: (r) => <span className="font-mono-kcc text-xs">{r.asset}</span> },
                        { key: "severity", label: "Sev", render: (r) => <StatusChip status={statusToChip(r.severity)}>{r.severity}</StatusChip> },
                        { key: "analyst", label: "Analyst" },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                    ]}
                    rows={(data?.events || []).slice(0, 40)}
                    testid="cyber-events"
                />
            </Panel>
        </div>
    );
}
